import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CheckboxInput,
  Form,
  TextInput,
  ColorSelect,
  Icon,
  ICONS,
  PLATFORM_THEME_COLORS,
  Typography,
  TYPOGRAPHY_SIZE,
  Button,
  DeleteModal,
  PopoverTooltip,
  SwitchInput,
} from '@thinknet-gmbh/platform-common-ui';
import { DEFAULT_NAME_FIELD_REGEX } from '@thinknet-gmbh/platform-common';

import { SETTINGS_PAGES } from 'src/pages/settings/settings.constants';

import {
  HeaderContainer,
  FormItemsWrapper,
  DeleteContainer,
  StyledBillableButton,
  StyledLabelContainer,
} from './common-page.styles';
import { COMMON_PAGE_TYPES_DATA, DEFAULT_ITEM_TO_DELETE } from './common-page.constants';
import { PageType } from './common-page.interfaces';

const CommonFormList = ({
  data,
  pageType,
  onDelete,
}: {
  data: any[];
  pageType: PageType;
  onDelete: (values: any) => void;
}) => {
  const { t } = useTranslation(['common', 'settings']);
  const [itemToDelete, setItemToDelete] = useState(DEFAULT_ITEM_TO_DELETE);
  const {
    formListName,
    infoLabel,
    addBtn,
    formItemLabel,
    placeholder,
    title,
  } = COMMON_PAGE_TYPES_DATA[pageType];

  const onSubmitDelete = (remove: (index: number) => void) => {
    onDelete(itemToDelete);
    remove(itemToDelete.removeKey);
    setItemToDelete(DEFAULT_ITEM_TO_DELETE);
  };

  return (
    <Form.List name={formListName} initialValue={data}>
      {(fields, { add, remove }) => (
        <div>
          {itemToDelete._id && (
            <DeleteModal
              isOpened
              translate={t}
              onClose={() => setItemToDelete(DEFAULT_ITEM_TO_DELETE)}
              onSubmit={() => onSubmitDelete(remove)}
              title={t('settings:companySettings.deleteAbsence.title')}
              subTitle={t('settings:companySettings.deleteAbsence.subtitle', {
                absenceName: itemToDelete.name,
              })}
            />
          )}
          <HeaderContainer>
            <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
              {pageType === SETTINGS_PAGES.absence ? t(infoLabel) : t(title)}
            </Typography>
            <Button
              type="primary"
              ghost
              icon={ICONS.plus}
              iconColor={PLATFORM_THEME_COLORS.primary.blue}
              onClick={() => add()}
            >
              {t(addBtn)}
            </Button>
          </HeaderContainer>
          <FormItemsWrapper>
            {fields.map((field) => (
              <div key={field.key}>
                <DeleteContainer>
                  <Form.Item
                    name={[field.name, 'name']}
                    label={t(formItemLabel)}
                    rules={[
                      {
                        required: true,
                        message:
                          pageType === SETTINGS_PAGES.absence
                            ? t('settings:companySettings.exceptions.requiredAbsenceReason')
                            : t('common:validation.error.labelRequired'),
                      },
                      {
                        pattern: DEFAULT_NAME_FIELD_REGEX,
                        message: t('common:validation.error.incorrectFormat'),
                      },
                    ]}
                  >
                    <TextInput placeholder={t(placeholder)} />
                  </Form.Item>
                  <Icon
                    icon={ICONS.trash}
                    color={PLATFORM_THEME_COLORS.tone.red}
                    size={22}
                    rightMargin={false}
                    onClick={() => {
                      const item = data[field.name];

                      if (item?._id) {
                        const { _id, name } = item;

                        return setItemToDelete({ _id, name, removeKey: field.name });
                      }

                      remove(field.name);
                    }}
                  />
                </DeleteContainer>
                {pageType === SETTINGS_PAGES.absence && (
                  <Fragment>
                    <Form.Item
                      name={[field.name, 'color']}
                      label={t('settings:companySettings.labels.absenceColor')}
                      rules={[
                        { required: true, message: t('common:validation.error.fieldRequired') },
                      ]}
                    >
                      <ColorSelect translate={t} placeholder={t(placeholder)} />
                    </Form.Item>
                    <StyledBillableButton>
                      <StyledLabelContainer>
                        <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                          {t('settings:companySettings.labels.absencePaymentStatus')}
                        </Typography>
                        <PopoverTooltip
                          placement="top"
                          overlayInnerStyle={{ width: '314px' }}
                          trigger={'hover'}
                          overlay={
                            <Typography fontColor={PLATFORM_THEME_COLORS.primary.white}>
                              {t('settings:companySettings.labels.popupOverlayText')}
                            </Typography>
                          }
                        >
                          <div>
                            <Icon
                              icon={ICONS.infoCircleRegular}
                              color={PLATFORM_THEME_COLORS.primary.blue}
                              size={20}
                            />
                          </div>
                        </PopoverTooltip>
                      </StyledLabelContainer>

                      <Form.Item name={[field.name, 'absencePaymentStatus']} valuePropName="checked">
                        <SwitchInput defaultChecked={false} />
                      </Form.Item>
                    </StyledBillableButton>
                  </Fragment>
                )}
                {pageType === SETTINGS_PAGES.documentsLabels && (
                  <Form.Item name={[field.name, 'availableForEmployee']} valuePropName="checked">
                    <CheckboxInput>
                      {t('settings:documentsLabels.availableForEmployee')}
                    </CheckboxInput>
                  </Form.Item>
                )}
              </div>
            ))}
          </FormItemsWrapper>
        </div>
      )}
    </Form.List>
  );
};

export default CommonFormList;
